import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DocumentUpload } from "@/components/ui/document-upload";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Lock, FileText, CheckCircle, Calendar, AlertTriangle, Users, User, Plus, Download, ChevronLeft, ChevronRight, Upload, ExternalLink, Eye, EyeOff, Link2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { apiRequest } from "@/lib/queryClient";
import { ComplianceChecklist, QuickTemplates, AuditLogViewer } from "@/components/compliance/ComplianceChecklists";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { useDocuments } from "@/hooks/useDocuments";
import { DocumentList } from "@/components/ui/document-list";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog as UIDialog,
  DialogContent as UIDialogContent,
  DialogHeader as UIDialogHeader,
  DialogTitle as UIDialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useMemo, useState } from "react";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import {
  classifyEmploymentType,
  formatHeadcountBreakdown,
  getComplianceEmployeeStats,
  scheduledWeeklyHours,
} from "@/lib/employeeHeadcount";
import { getUpcomingComplianceTasks } from "@/lib/complianceTasks";

export default function Compliance() {
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const clientId = (user as any)?.clientId as number | undefined;
  const effectiveClientId = clientId ?? 1;
  const [openAdd, setOpenAdd] = useState(false);
  const [newItem, setNewItem] = useState({ title: "", category: "general", dueDate: "" });
  // HIPAA / ERISA confirmations persisted to server
  const [hipaaOpen, setHipaaOpen] = useState(false);
  const [erisaOpen, setErisaOpen] = useState(false);
  const [hipaaChecks, setHipaaChecks] = useState({ privacy: false, security: false, baas: false });
  const [hipaaConfirmedAt, setHipaaConfirmedAt] = useState<string | null>(null);
  const [erisaChecks, setErisaChecks] = useState({ planDocs: false, spd: false, form5500: false });
  const [erisaConfirmedAt, setErisaConfirmedAt] = useState<string | null>(null);
  
  // Document upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    type: 'file' as 'file' | 'link',
    linkUrl: '',
    areaKey: 'general',
    visibility: 'internal' as 'internal' | 'employee',
    fileName: '',
    documentName: '',
    fileType: '' as string,
  });

  const COMPLIANCE_FILE_TYPES = [
    { value: 'sbc', label: 'Summary of Benefits and Coverage (SBC)' },
    { value: 'spd', label: 'Summary Plan Description (SPD)' },
    { value: 'form5500', label: 'Form 5500' },
    { value: 'wrap_document', label: 'Wrap Document' },
    { value: 'hipaa_notice', label: 'HIPAA Notice of Privacy Practices' },
    { value: 'cobra_notice', label: 'COBRA Notice' },
    { value: 'medicare_notice', label: 'Medicare Part D Notice' },
    { value: 'womens_health', label: "Women's Health and Cancer Rights Notice" },
    { value: 'chip_notice', label: 'CHIP Notice' },
    { value: 'exchange_notice', label: 'Health Insurance Marketplace Notice' },
    { value: 'saa', label: 'Summary Annual Report (SAR)' },
    { value: 'plan_amendment', label: 'Plan Amendment' },
    { value: 'certificate', label: 'Certificate of Coverage' },
    { value: 'other', label: 'Other Compliance Document' },
  ];
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const { data: employeesData = [] } = useQuery<any[]>({
    queryKey: ["/api/employees"],
  });

  // Same counting rules as the broker Compliance tab (DEV-362): headcount
  // only (terminated etc. excluded); blank employment type is "Type Not Set".
  const stats = useMemo(
    () => getComplianceEmployeeStats(sortEmployeesByFullName(employeesData)),
    [employeesData]
  );
  const employees = stats.employees;

  const { data: clientRecord } = useQuery<any>({
    queryKey: ["/api/clients", String(clientId)],
    queryFn: async () => {
      const r = await fetch(`/api/clients/${clientId}`, { credentials: "include" });
      if (!r.ok) return null;
      return r.json();
    },
    enabled: !!clientId,
  });
  const clientName: string | undefined = clientRecord?.name || clientRecord?.companyName;

  // Fetch stored compliance status from backend (calculated annually on Jan 1)
  const { data: complianceStatus } = useQuery<{
    aleStatus: boolean | null;
    cobraStatus: boolean | null;
    aleStatusDeterminedAt: Date | null;
    cobraStatusDeterminedAt: Date | null;
  }>({
    queryKey: ["/api/compliance/status", effectiveClientId],
    enabled: !!effectiveClientId,
  });

  // Calculate compliance determinations (use stored status from backend with fallback)
  const totalEmployees = stats.headcount.total;
  const fullTimeEmployees = stats.fullTime;
  const partTimeEmployees = stats.partTime;
  const notSetEmployees = stats.notSet;
  const totalPartTimeHoursPerMonth = stats.partTimeHoursPerMonth;
  const partTimeFTE = stats.partTimeFte;
  const totalFTE = stats.totalFte;
  // Missing hours only matter for ALE clients (only they face 1H/4980H
  // ACA exposure) - the warning below is gated on isALE.
  const employeesMissingScheduledHours = employees.filter(
    (e: any) => !e.scheduledHours || !String(e.scheduledHours).trim()
  );

  // ALE status comes from the ACA engine (prior-year FT + FTE), never from a
  // local headcount formula. Estimated when prior-year hours are incomplete.
  const acaTaxYear = new Date().getFullYear();
  const { data: acaDashboard } = useQuery<{ ale?: { isAle: boolean; averageFte: number; estimated: boolean; basisYear: number; source: string } }>({
    queryKey: ["/api/aca/dashboard", effectiveClientId, acaTaxYear],
    queryFn: async () => {
      const res = await fetch(`/api/aca/dashboard?clientId=${effectiveClientId}&taxYear=${acaTaxYear}`, { credentials: "include" });
      if (!res.ok) return {};
      return res.json();
    },
    enabled: !!effectiveClientId,
  });
  const isALE = !!acaDashboard?.ale?.isAle;
  const aleFteDisplay = acaDashboard?.ale
    ? `${acaDashboard.ale.averageFte} FT + FTE (${acaDashboard.ale.basisYear}${acaDashboard.ale.estimated ? ", estimated" : ""})`
    : "Not computed";
  const isFederalCOBRASubject = complianceStatus?.cobraStatus ?? (totalEmployees >= 20);
  const isFMLASubject = totalEmployees >= 50;

  // Fetch compliance documents for this employer's client
  const {
    documents: complianceDocs = [],
    loading: docsLoading,
    error: docsError,
    refreshDocuments,
  } = useDocuments({
    ownerType: "client",
    clientId,
    category: "compliance",
    autoRefresh: true,
  });

  // Fetch evidence with checklist details
  const { data: evidenceWithDetails = [], refetch: refetchEvidence } = useQuery<any[]>({
    queryKey: ['/api/compliance/evidence/with-details', effectiveClientId],
    queryFn: async () => {
      const res = await fetch(`/api/compliance/evidence/with-details?clientId=${effectiveClientId}`, { credentials: 'include' });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!effectiveClientId,
  });

  const handleUploadDocument = async () => {
    if (uploadForm.type === 'link' && !uploadForm.linkUrl) {
      toast({ title: "Error", description: "Please enter a URL", variant: "destructive" });
      return;
    }
    if (uploadForm.type === 'file' && !uploadFile) {
      toast({ title: "Error", description: "Please select a file", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      if (uploadForm.type === 'link') {
        await apiRequest('POST', '/api/compliance/evidence', {
          clientId: effectiveClientId,
          type: 'link',
          linkUrl: uploadForm.linkUrl,
          fileName: uploadForm.documentName || uploadForm.linkUrl,
          fileType: uploadForm.fileType || null,
          visibility: uploadForm.visibility,
        });
      } else if (uploadFile) {
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('clientId', String(effectiveClientId));
        formData.append('visibility', uploadForm.visibility);
        formData.append('documentName', uploadForm.documentName || uploadFile.name);
        formData.append('fileType', uploadForm.fileType || '');

        const res = await fetch('/api/compliance/evidence/upload', {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        if (!res.ok) throw new Error('Upload failed');
      }

      refetchEvidence();
      setShowUploadDialog(false);
      setUploadForm({ type: 'file', linkUrl: '', areaKey: 'general', visibility: 'internal', fileName: '', documentName: '', fileType: '' });
      setUploadFile(null);
      toast({ title: "Success", description: "Document uploaded successfully" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Upload failed", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadEvidence = async (ev: any) => {
    if (ev.type === 'link' && ev.linkUrl) {
      window.open(ev.linkUrl, '_blank');
    } else if (ev.s3Key) {
      const res = await fetch(`/api/compliance/evidence/${ev.id}/download`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        window.open(data.url, '_blank');
      }
    }
  };

  // Group documents by complianceType tag
  const docsByType: Record<string, any[]> = (complianceDocs || []).reduce(
    (acc: Record<string, any[]>, doc: any) => {
      const tag = (doc.tags || []).find((t: string) =>
        t.startsWith("complianceType:")
      );
      const type = tag ? tag.split(":")[1] : "other";
      if (!acc[type]) acc[type] = [];
      acc[type].push(doc);
      return acc;
    },
    {}
  );

  const [selectedType, setSelectedType] = useState<string | null>(null);
  // Load persisted checklists
  const { data: hipaaChecklist } = useQuery<any | null>({
    queryKey: ["/api/compliance/checklists", effectiveClientId, "hipaa"],
    enabled: !!effectiveClientId,
    queryFn: async () => {
      const r = await fetch(`/api/compliance/checklists?clientId=${effectiveClientId}&type=hipaa`);
      if (!r.ok) throw new Error("Failed to load HIPAA checklist");
      const rows = await r.json();
      return rows[0] || null;
    }
  });
  const { data: erisaChecklist } = useQuery<any | null>({
    queryKey: ["/api/compliance/checklists", effectiveClientId, "erisa"],
    enabled: !!effectiveClientId,
    queryFn: async () => {
      const r = await fetch(`/api/compliance/checklists?clientId=${effectiveClientId}&type=erisa`);
      if (!r.ok) throw new Error("Failed to load ERISA checklist");
      const rows = await r.json();
      return rows[0] || null;
    }
  });

  useEffect(() => {
    if (hipaaChecklist) {
      setHipaaChecks(hipaaChecklist.checks || { privacy: false, security: false, baas: false });
      setHipaaConfirmedAt(hipaaChecklist.confirmedAt || null);
    }
    if (erisaChecklist) {
      setErisaChecks(erisaChecklist.checks || { planDocs: false, spd: false, form5500: false });
      setErisaConfirmedAt(erisaChecklist.confirmedAt || null);
    }
  }, [hipaaChecklist, erisaChecklist]);

  const upsertChecklist = useMutation({
    mutationFn: async (payload: { type: 'hipaa'|'erisa'; checks: any; confirmedAt?: string }) => {
      const body = { clientId: effectiveClientId, type: payload.type, checks: payload.checks, confirmedAt: payload.confirmedAt || new Date().toISOString(), confirmedBy: user?.name || 'Employer Admin' };
      const r = await fetch('/api/compliance/checklists', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error('Checklist save failed');
      return r.json();
    },
    onSuccess: (_, vars) => {
      if (vars.type === 'hipaa') {
        queryClient.invalidateQueries({ queryKey: ["/api/compliance/checklists", effectiveClientId, "hipaa"] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/compliance/checklists", effectiveClientId, "erisa"] });
      }
    }
  });

  const hasErisaDocs = (docsByType["erisa"] || []).length > 0;
  const hasSpdDoc = (docsByType["erisa"] || []).some((d:any) => (d.tags||[]).some((t:string)=>/spd/i.test(t) || /summary plan/i.test(t)));
  useEffect(() => {
    // Auto-reflect doc presence in ERISA checks (non-destructive)
    setErisaChecks(prev => ({ ...prev, planDocs: prev.planDocs || hasErisaDocs, spd: prev.spd || hasSpdDoc }));
  }, [hasErisaDocs, hasSpdDoc]);

  const hipaaCompliant = hipaaChecks.privacy && hipaaChecks.security && hipaaChecks.baas;
  const erisaCompliant = erisaChecks.planDocs && erisaChecks.spd && erisaChecks.form5500;

  // Persisted compliance items
  const { data: complianceItems = [] } = useQuery<any[]>({
    queryKey: ["/api/compliance/items", effectiveClientId],
    queryFn: async () => {
      const res = await fetch(`/api/compliance/items?clientId=${effectiveClientId}`);
      if (!res.ok) throw new Error("Failed to load compliance items");
      return res.json();
    }
  });

  const upcomingTasks = useMemo(() => getUpcomingComplianceTasks(complianceItems), [complianceItems]);

  // Checklist completion summary by area
  const { data: checklistSummary = {} } = useQuery<Record<string, { total: number; completed: number; isComplete: boolean }>>({
    queryKey: ["/api/compliance/checklist-summary", effectiveClientId],
    enabled: !!effectiveClientId,
    queryFn: async () => {
      const res = await fetch(`/api/compliance/checklist-summary?clientId=${effectiveClientId}`, { credentials: 'include' });
      if (!res.ok) return {};
      return res.json();
    }
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      const body = { ...newItem, clientId: effectiveClientId, dueDate: newItem.dueDate || null };
      const res = await fetch('/api/compliance/items', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Create failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/items", effectiveClientId] });
      setOpenAdd(false);
      setNewItem({ title: "", category: "general", dueDate: "" });
    }
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Compliance</h1>
      </div>

      {/* Employee Statistics Overview */}
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Employee Statistics Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {(() => {
              // Keep underlying FTE unrounded; display with 2 decimals
              const fullTimeEquivalentsDisplay = Number(partTimeFTE.toFixed(2));

              return (
                <>
                  <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border" data-testid="compliance-total-employees">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {totalEmployees}
                    </div>
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      Total Employee Count
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatHeadcountBreakdown(stats.headcount)}
                    </div>
                  </div>

                  <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {fullTimeEmployees.length}
                    </div>
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      Full-Time Employee Count
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      40+ scheduled hours/week
                    </div>
                  </div>

                  <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border">
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {partTimeEmployees.length}
                    </div>
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      Part-Time Employee Count
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Less than 40 hours/week
                    </div>
                  </div>

                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg border" data-testid="compliance-not-set">
                    <div className="text-2xl font-bold text-gray-600 dark:text-gray-300">
                      {notSetEmployees.length}
                    </div>
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      Type Not Set
                    </div>
                    {notSetEmployees.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Set an employment type to include in FTE
                      </div>
                    )}
                  </div>

                  <div className="text-center p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {Math.round(totalPartTimeHoursPerMonth)}
                    </div>
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      Part-Time Employee Hours
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Total hours per month
                    </div>
                  </div>

                  <div className="text-center p-4 bg-teal-50 dark:bg-teal-950/20 rounded-lg border">
                    <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                      {fullTimeEquivalentsDisplay}
                    </div>
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      Full-Time Equivalents
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Part-time hours ÷ 130
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Detailed breakdown */}
          <div className="mt-6 pt-6 border-t">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
              Employee Hours Breakdown
            </h4>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {employees.map((employee: any) => {
                const scheduledHours = scheduledWeeklyHours(employee);
                const monthlyHours = Math.round(scheduledHours * 4.33);
                const employmentClass = classifyEmploymentType(employee);

                return (
                  <div
                    key={employee.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <User className="h-4 w-4 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {employee.firstName} {employee.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {employee.department || "Unassigned"} •{" "}
                          {employee.position || "No Title"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={employmentClass === "full_time" ? "default" : employmentClass === "part_time" ? "secondary" : "outline"}
                          className="text-xs"
                        >
                          {employmentClass === "full_time" ? "Full-Time" : employmentClass === "part_time" ? "Part-Time" : "Type Not Set"}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {scheduledHours}h/week • {monthlyHours}h/month
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compliance Validator */}
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Compliance Requirements Validator
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            const partTimeFTEDisplay = Number(partTimeFTE.toFixed(2));
            const totalFTEDisplay = Number(totalFTE.toFixed(2));

            // Additional compliance determinations (not stored in backend)
            const isHIPAASubject = totalEmployees >= 1; // All group health plans
            const isERISASubject = totalEmployees >= 1; // All employee benefit plans
            const isACAReportingSubject = isALE;

            const complianceRequirements = [
              {
                name: "Applicable Large Employer (ALE)",
                description: "50+ full-time equivalent employees",
                isSubject: isALE,
                threshold: "50 FT + FTE (prior year)",
                current: aleFteDisplay,
                details: "Must offer minimum essential coverage or pay penalty",
                areaKey: "aca",
              },
              {
                name: "HIPAA",
                description:
                  "Health Insurance Portability and Accountability Act",
                isSubject: isHIPAASubject,
                threshold: "1+ employees",
                current: `${totalEmployees} employees`,
                details: "Privacy and security rules for health information",
                areaKey: "hipaa",
              },
              {
                name: "ERISA",
                description: "Employee Retirement Income Security Act",
                isSubject: isERISASubject,
                threshold: "1+ employees",
                current: `${totalEmployees} employees`,
                details:
                  "Fiduciary responsibilities and reporting requirements",
                areaKey: "erisa",
              },
              {
                name: "Federal COBRA",
                description: "Consolidated Omnibus Budget Reconciliation Act",
                isSubject: isFederalCOBRASubject,
                threshold: "20+ employees",
                current: `${totalEmployees} employees`,
                details:
                  "Continuation coverage rights for terminated employees",
                areaKey: "cobra",
              },
              {
                name: "FMLA",
                description: "Family and Medical Leave Act",
                isSubject: isFMLASubject,
                threshold: "50+ employees",
                current: `${totalEmployees} employees`,
                details: "Unpaid leave protection for eligible employees",
                areaKey: "fmla",
              },
              {
                name: "ACA Employer Reporting",
                description: "Forms 1094-C and 1095-C filing requirements",
                isSubject: isACAReportingSubject,
                threshold: "ALE (50+ FT + FTE)",
                current: aleFteDisplay,
                details: "Annual reporting of health coverage offered",
                areaKey: "aca",
              },
            ];

            const getCardStyle = (req: typeof complianceRequirements[0]) => {
              if (!req.isSubject) {
                return "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/20";
              }
              const summary = checklistSummary[req.areaKey];
              const isComplete = summary?.isComplete || false;
              if (isComplete) {
                return "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20";
              }
              return "border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20";
            };

            return (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {complianceRequirements.map((req, index) => {
                    const summary = checklistSummary[req.areaKey];
                    const isComplete = summary?.isComplete || false;
                    const hasChecklist = summary && summary.total > 0;
                    
                    return (
                      <div
                        key={index}
                        className={`p-4 rounded-lg border-2 ${getCardStyle(req)}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-sm">{req.name}</h4>
                          <div className="flex flex-col items-end gap-1">
                            <Badge
                              variant={req.isSubject ? "secondary" : "outline"}
                              className="text-xs"
                            >
                              {req.isSubject ? "Subject" : "Not Subject"}
                            </Badge>
                            {req.isSubject && hasChecklist && (
                              <Badge
                                variant={isComplete ? "default" : "secondary"}
                                className={`text-xs ${isComplete ? "bg-green-600 hover:bg-green-700" : "bg-orange-500 hover:bg-orange-600 text-white"}`}
                              >
                                {isComplete ? "Complete" : "Incomplete"}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                          {req.description}
                        </p>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Threshold:</span>
                            <span className="font-medium">{req.threshold}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Current:</span>
                            <span className="font-medium">{req.current}</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 italic">
                          {req.details}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {/* Summary Section */}
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    {clientName ? `Compliance Summary for ${clientName}` : "Compliance Summary"}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-200">
                        Current Status:
                      </p>
                      <ul className="mt-1 space-y-1 text-xs">
                        <li>• Total Employees: {totalEmployees}</li>
                        <li>
                          • Full-Time Employees: {fullTimeEmployees.length}
                        </li>
                        <li>• Part-Time Employees: {partTimeEmployees.length}</li>
                        {notSetEmployees.length > 0 && (
                          <li>• Type Not Set: {notSetEmployees.length}</li>
                        )}
                        <li>• Part-Time FTE: {partTimeFTEDisplay}</li>
                        <li>
                          • <strong>Total FTE: {totalFTEDisplay}</strong>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-200">
                        Active Compliance Requirements:
                      </p>
                      <ul className="mt-1 space-y-1 text-xs">
                        {complianceRequirements
                          .filter((req) => req.isSubject)
                          .map((req, index) => (
                            <li key={index}>• {req.name}</li>
                          ))}
                      </ul>
                    </div>
                  </div>

                  {isALE && (
                    <div className="mt-4 p-3 bg-orange-100 dark:bg-orange-900/30 rounded border border-orange-200 dark:border-orange-800">
                      <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                        ⚠️ Applicable Large Employer (ALE) Status
                      </p>
                      <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                        This employer may be subject to ACA employer mandate penalties if minimum essential coverage and minimum value plans are not offered, or if the plan is deemed unaffordable according to ACA standards.
                      </p>
                    </div>
                  )}

                  {isALE && employeesMissingScheduledHours.length > 0 && (
                    <div className="mt-4 p-3 bg-amber-100 dark:bg-amber-900/30 rounded border border-amber-200 dark:border-amber-800">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        {employeesMissingScheduledHours.length} employee
                        {employeesMissingScheduledHours.length === 1 ? "" : "s"} missing scheduled hours
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                        ACA full-time status is based on 30 hrs/week (130/month). As an Applicable Large Employer, this client can't verify ACA eligibility for these employees until scheduled hours are entered.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Enhanced Compliance Checklists */}
      {effectiveClientId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Compliance Checklists
              </span>
              <AuditLogViewer clientId={effectiveClientId} />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <QuickTemplates />
            {isFederalCOBRASubject && <ComplianceChecklist clientId={effectiveClientId} areaKey="cobra" />}
            <ComplianceChecklist clientId={effectiveClientId} areaKey="hipaa" />
            <ComplianceChecklist clientId={effectiveClientId} areaKey="erisa" />
            {isALE && <ComplianceChecklist clientId={effectiveClientId} areaKey="aca" />}
            {isFMLASubject && <ComplianceChecklist clientId={effectiveClientId} areaKey="fmla" />}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* COBRA Compliance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              COBRA Compliance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">COBRA Administration</span>
              <Badge
                variant={
                  (docsByType["cobra"] || []).length > 0
                    ? "default"
                    : "secondary"
                }
              >
                {(docsByType["cobra"] || []).length > 0
                  ? "Docs Uploaded"
                  : "Missing"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                Notification Requirements
              </span>
              <Badge
                variant={
                  (docsByType["cobra"] || []).length > 0
                    ? "default"
                    : "secondary"
                }
              >
                {(docsByType["cobra"] || []).length > 0
                  ? "Up to Date"
                  : "Action Needed"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Continuation Coverage</span>
              <Badge
                variant={
                  (docsByType["cobra"] || []).length > 1
                    ? "default"
                    : "secondary"
                }
              >
                {(docsByType["cobra"] || []).length > 1
                  ? "Documented"
                  : "Review Needed"}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() =>
                  setLocation("/compliance/cobra-details?role=employer")
                }
              >
                View COBRA Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setSelectedType("cobra")}
              >
                Manage
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* HIPAA Compliance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              HIPAA Compliance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Status</span>
              <Badge variant={hipaaCompliant ? 'default' : 'secondary'}>
                {hipaaCompliant ? 'Subject & Compliant' : 'Action Needed'}
              </Badge>
            </div>
            {hipaaConfirmedAt && (
              <p className="text-xs text-muted-foreground">Last confirmed: {new Date(hipaaConfirmedAt).toLocaleDateString()}</p>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Privacy Policies</span>
              <Badge variant={hipaaChecks.privacy ? 'default' : 'secondary'}>
                {hipaaChecks.privacy ? 'Confirmed' : 'Missing'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Security Safeguards</span>
              <Badge variant={hipaaChecks.security ? 'default' : 'secondary'}>
                {hipaaChecks.security ? 'Confirmed' : 'Action Needed'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                Business Associate Agreements
              </span>
              <Badge variant={hipaaChecks.baas ? 'default' : 'secondary'}>
                {hipaaChecks.baas ? 'Signed' : 'Pending'}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setHipaaOpen(true)}
              >
                View HIPAA Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setSelectedType("hipaa")}
              >
                Manage
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ERISA Compliance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              ERISA Compliance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Status</span>
              <Badge variant={erisaCompliant ? 'default' : 'secondary'}>
                {erisaCompliant ? 'Subject & Compliant' : 'Action Needed'}
              </Badge>
            </div>
            {erisaConfirmedAt && (
              <p className="text-xs text-muted-foreground">Last confirmed: {new Date(erisaConfirmedAt).toLocaleDateString()}</p>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Plan Documents</span>
              <Badge variant={erisaChecks.planDocs ? 'default' : 'secondary'}>
                {erisaChecks.planDocs ? 'Current' : 'Missing'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                Summary Plan Descriptions
              </span>
              <Badge variant={erisaChecks.spd ? 'default' : 'secondary'}>
                {erisaChecks.spd ? 'Distributed' : 'Pending'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Form 5500 Filing</span>
              <Badge variant={erisaChecks.form5500 ? 'default' : 'secondary'}>
                {erisaChecks.form5500 ? 'Filed' : 'Due Soon'}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setErisaOpen(true)}
              >
                View ERISA Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setSelectedType("erisa")}
              >
                Manage
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ACA Compliance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              ACA Compliance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Employer Mandate</span>
              <Badge
                variant={
                  (docsByType["aca"] || []).length > 0 ? "default" : "secondary"
                }
              >
                {(docsByType["aca"] || []).length > 0 ? "Compliant" : "Review"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Forms 1094-C & 1095-C</span>
              <Badge
                variant={
                  (docsByType["aca"] || []).length > 1 ? "default" : "secondary"
                }
              >
                {(docsByType["aca"] || []).length > 1 ? "Filed" : "Pending"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Affordability Testing</span>
              <Badge
                variant={
                  (docsByType["aca"] || []).length > 2 ? "default" : "secondary"
                }
              >
                {(docsByType["aca"] || []).length > 2
                  ? "Passed"
                  : "Action Needed"}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() =>
                  setLocation("/aca")
                }
              >
                View ACA Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setSelectedType("aca")}
              >
                Manage
              </Button>
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <Button
                variant="default"
                size="sm"
                className="w-full"
                onClick={() => setLocation("/aca")}
              >
                <FileText className="h-4 w-4 mr-2" />
                View ACA Forms
              </Button>
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <Button
                variant="default"
                size="sm"
                className="w-full bg-primary hover:bg-primary/90"
                onClick={() => setLocation("/aca?role=employer")}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open ACA Module
              </Button>
            </div>
          </CardContent>
        </Card>
        

      </div>

      {/* Compliance Tasks */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Compliance Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingTasks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming compliance tasks.</p>
          ) : (
            <div className="space-y-4">
              {upcomingTasks.map((task) => (
                <div key={task.item.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {task.daysUntilDue < 0 ? (
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                    ) : (
                      <Calendar className="h-5 w-5 text-orange-500" />
                    )}
                    <div>
                      <p className="font-medium">{task.item.title}</p>
                      <p className="text-sm text-gray-500 capitalize">{task.item.category}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={task.daysUntilDue < 0 ? "destructive" : "secondary"}>
                      Due: {format(task.dueDate, "MMMM d, yyyy")}
                    </Badge>
                    <p className="text-xs text-gray-500 mt-1">{task.dueLabel}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Templates */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Templates</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Button asChild variant="outline" className="justify-start">
              <a href="#" download>
                <FileText className="h-4 w-4 mr-2" /> COBRA Initial Notice (Template)
              </a>
            </Button>
            <Button asChild variant="outline" className="justify-start">
              <a href="#" download>
                <FileText className="h-4 w-4 mr-2" /> COBRA Election Notice (Template)
              </a>
            </Button>
            <Button asChild variant="outline" className="justify-start">
              <a href="#" download>
                <FileText className="h-4 w-4 mr-2" /> 1095-C Sample (Reference)
              </a>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Upload completed versions under Compliance Documents.</p>
        </CardContent>
      </Card>

      {/* Simple list of persisted compliance items below existing documents */}
      <Card>
        <CardHeader>
          <CardTitle>Tracked Compliance Items</CardTitle>
        </CardHeader>
        <CardContent>
          {complianceItems.length === 0 && (
            <p className="text-sm text-muted-foreground">No compliance items found.</p>
          )}
          <div className="space-y-2">
            {complianceItems.map((item:any) => (
              <div key={item.id} className="flex items-center justify-between p-3 border rounded">
                <div>
                  <p className="font-medium text-sm">{item.title}</p>
                  <p className="text-xs text-gray-500">Category: {item.category} {item.dueDate ? `• Due ${new Date(item.dueDate).toLocaleDateString()}` : ''}</p>
                </div>
                <Badge variant={item.status === 'completed' ? 'default' : item.status === 'in_progress' ? 'secondary' : 'outline'} className="text-xs capitalize">{item.status}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Compliance Documents */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Compliance Documents</CardTitle>
          <Button variant="outline" size="sm" onClick={() => setShowUploadDialog(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </CardHeader>
        <CardContent>
          {docsError && (
            <div className="text-sm text-red-600 mb-4">{docsError}</div>
          )}
          
          {evidenceWithDetails.length === 0 && (complianceDocs || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No compliance documents found. Upload documents or attach evidence to checklist items.</p>
          ) : (
            <div className="space-y-4">
              {/* Checklist Evidence */}
              {evidenceWithDetails.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Checklist Evidence</h4>
                  {evidenceWithDetails.map((ev: any) => (
                    <div key={`ev-${ev.id}`} className="flex items-center justify-between p-3 border rounded hover:bg-muted/50">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {ev.type === 'link' ? (
                          <Link2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                        ) : (
                          <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{ev.fileName || ev.linkUrl || 'Document'}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            {ev.areaKey && (
                              <Badge variant="outline" className="text-xs uppercase">{ev.areaKey}</Badge>
                            )}
                            {ev.formType && (
                              <Badge variant="secondary" className="text-xs">{ev.formType}</Badge>
                            )}
                            {ev.checklistItemTitle && !ev.formType && (
                              <span className="text-xs text-muted-foreground">{ev.checklistItemTitle}</span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {ev.createdAt ? new Date(ev.createdAt).toLocaleDateString() : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {ev.visibility === 'employee' ? (
                          <Badge variant="default" className="text-xs flex items-center gap-1">
                            <Eye className="h-3 w-3" /> Employee
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            <EyeOff className="h-3 w-3" /> Internal
                          </Badge>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadEvidence(ev)}>
                          {ev.type === 'link' ? <ExternalLink className="h-4 w-4" /> : <Download className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Legacy Compliance Documents */}
              {(complianceDocs || []).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Uploaded Documents</h4>
                  <DocumentList
                    documents={complianceDocs as any}
                    onDocumentUpdate={refreshDocuments}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Compliance Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <RadioGroup 
                value={uploadForm.type} 
                onValueChange={(v) => setUploadForm(f => ({ ...f, type: v as 'file' | 'link' }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="file" id="type-file" />
                  <Label htmlFor="type-file">File Upload</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="link" id="type-link" />
                  <Label htmlFor="type-link">External Link</Label>
                </div>
              </RadioGroup>
            </div>

            {uploadForm.type === 'link' ? (
              <>
                <div className="space-y-2">
                  <Label>Link URL</Label>
                  <Input
                    placeholder="https://example.com/document"
                    value={uploadForm.linkUrl}
                    onChange={(e) => setUploadForm(f => ({ ...f, linkUrl: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Document Name</Label>
                  <Input
                    placeholder="Enter a name for this document"
                    value={uploadForm.documentName}
                    onChange={(e) => setUploadForm(f => ({ ...f, documentName: e.target.value }))}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Select File</Label>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  />
                  <p className="text-xs text-muted-foreground">Accepted: PDF, Word, Excel, Images</p>
                </div>
                <div className="space-y-2">
                  <Label>Document Name</Label>
                  <Input
                    placeholder="Enter a friendly name for this document"
                    value={uploadForm.documentName}
                    onChange={(e) => setUploadForm(f => ({ ...f, documentName: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">Leave blank to use the original file name</p>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>File Type</Label>
              <Select 
                value={uploadForm.fileType} 
                onValueChange={(v) => setUploadForm(f => ({ ...f, fileType: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {COMPLIANCE_FILE_TYPES.map((ft) => (
                    <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
              <div>
                <p className="text-sm font-medium">Employee Visibility</p>
                <p className="text-xs text-muted-foreground">Allow employees to view this document</p>
              </div>
              <Switch
                checked={uploadForm.visibility === 'employee'}
                onCheckedChange={(checked) => setUploadForm(f => ({ ...f, visibility: checked ? 'employee' : 'internal' }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadDocument} disabled={isUploading}>
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage documents for a specific compliance type */}
      <UIDialog
        open={!!selectedType}
        onOpenChange={() => setSelectedType(null)}
      >
        <UIDialogContent className="max-w-2xl">
          <UIDialogHeader>
            <UIDialogTitle>
              Manage {(selectedType || "").toUpperCase()} Documents
            </UIDialogTitle>
          </UIDialogHeader>
          <div className="space-y-4">
            <DocumentUpload
              ownerType={clientId ? "client" : "agency"}
              clientId={clientId}
              defaultCategory="compliance"
              fixedCategory
              complianceTypeOptions={[
                { value: "aca", label: "ACA" },
                { value: "cobra", label: "COBRA" },
                { value: "hipaa", label: "HIPAA" },
                { value: "erisa", label: "ERISA" },
                { value: "fmla", label: "FMLA" },
                { value: "osha", label: "OSHA" },
                { value: "state", label: "State" },
                { value: "other", label: "Other" },
              ]}
              trigger={
                <Button variant="outline">
                  <FileText className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              }
              onUploadSuccess={() => refreshDocuments()}
            />
            <DocumentList
              documents={
                (complianceDocs || []).filter((d: any) =>
                  (d.tags || []).some(
                    (t: string) => t === `complianceType:${selectedType}`
                  )
                ) as any
              }
              onDocumentUpdate={refreshDocuments}
            />
          </div>
        </UIDialogContent>
      </UIDialog>

      {/* HIPAA Checklist Modal */}
      <Dialog open={hipaaOpen} onOpenChange={setHipaaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>HIPAA Compliance Checklist</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={hipaaChecks.privacy} onChange={e=> setHipaaChecks(c=>({ ...c, privacy: e.target.checked }))} />
              Privacy policies are published
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={hipaaChecks.security} onChange={e=> setHipaaChecks(c=>({ ...c, security: e.target.checked }))} />
              Security safeguards implemented
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={hipaaChecks.baas} onChange={e=> setHipaaChecks(c=>({ ...c, baas: e.target.checked }))} />
              BAAs are signed
            </label>
            {hipaaConfirmedAt && (
              <p className="text-xs text-muted-foreground">Last confirmed: {new Date(hipaaConfirmedAt).toLocaleString()}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={()=> setHipaaOpen(false)}>Close</Button>
              <Button size="sm" disabled={upsertChecklist.isPending} onClick={()=> {
                const confirmedAt = new Date().toISOString();
                setHipaaConfirmedAt(confirmedAt);
                upsertChecklist.mutate({ type: 'hipaa', checks: hipaaChecks, confirmedAt });
                setHipaaOpen(false);
              }}>Confirm</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ERISA Checklist Modal */}
      <Dialog open={erisaOpen} onOpenChange={setErisaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ERISA Compliance Checklist</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!erisaChecks.planDocs} onChange={e=> setErisaChecks(c=>({ ...c, planDocs: e.target.checked }))} />
              Plan documents are current
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!erisaChecks.spd} onChange={e=> setErisaChecks(c=>({ ...c, spd: e.target.checked }))} />
              SPD is present
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={erisaChecks.form5500} onChange={e=> setErisaChecks(c=>({ ...c, form5500: e.target.checked }))} />
              Form 5500 is filed
            </label>
            {erisaConfirmedAt && (
              <p className="text-xs text-muted-foreground">Last confirmed: {new Date(erisaConfirmedAt).toLocaleString()}</p>
            )}
            <div className="flex justify-between items-center">
              <p className="text-xs text-muted-foreground">Docs detected: {(docsByType['erisa']||[]).length}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={()=> setErisaOpen(false)}>Close</Button>
                <Button size="sm" disabled={upsertChecklist.isPending} onClick={()=> {
                  const confirmedAt = new Date().toISOString();
                  setErisaConfirmedAt(confirmedAt);
                  upsertChecklist.mutate({ type: 'erisa', checks: erisaChecks, confirmedAt });
                  setErisaOpen(false);
                }}>Confirm</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
